import { TrueBase, css, html } from "../TrueBase/TrueBase";

/**
 * @customelement - true-slide-in-panel
 * @description
 * Panel that slides into the view
 *
 * ### Properties
 *
 *
 */
export class TrueSlideInPanel extends TrueBase {
  static get properties() {
    return {
      active: {
        type: Boolean,
        reflect: true,
      },
      hasOwnTrigger: {
        type: Boolean,
      },
      position: {
        type: String,
        reflect: true,
      },
      fullWidth: {
        type: Boolean,
      },
      styledScrollbar: {
        type: Boolean,
      },
      slidePanel: {
        type: Object,
        attribute: false,
      },
      hash: {
        type: String,
        attribute: false,
      },
      openEvent: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      beforeOpenEvent: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      closeEvent: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      beforeCloseEvent: {
        type: Object,
        reflect: false,
        attribute: false,
      },
      closeOnOutsideClick: {
        type: Boolean,
      },
    };
  }

  constructor() {
    super();
    this.active = false;
    this.position = "right";
  }

  connectedCallback() {
    super.connectedCallback();
    this.openEvent = new CustomEvent("tp-slidepanel-open", {
      bubbles: false,
      cancelable: true,
      detail: {
        element: this,
      },
    });
    this.beforeOpenEvent = new CustomEvent("tp-slidepanel-before-open", {
      bubbles: false,
      cancelable: true,
      detail: {
        element: this,
      },
    });
    this.closeEvent = new CustomEvent("tp-slidepanel-closed", {
      bubbles: false,
      cancelable: true,
      detail: {
        element: this,
      },
    });
    this.beforeCloseEvent = new CustomEvent("tp-slidepanel-before-closed", {
      bubbles: false,
      cancelable: true,
      detail: {
        element: this,
      },
    });
  }

  firstUpdated() {
    const slots = this.shadowRoot.querySelectorAll("slot");

    if (
      document.documentMode ||
      (/Edge/.test(navigator.userAgent) && !/chrome/.test(navigator.userAgent))
    ) {
      const trigger = this.shadowRoot.querySelector(".slideinpanel-trigger");
      const overlay = this.shadowRoot.querySelector(".slideinpanel-overlay");
      // trigger.addEventListener("click", () => {
      //   this.toggleSlidePanel();
      // });

      if (this.closeOnOutsideClick && overlay) {
        overlay.addEventListener("click", () => {
          this._beforeClose();
        });
      }
    }

    slots[3].addEventListener("slotchange", () => {
      let headingOne = slots[3]
        .assignedNodes()
        .filter((n) => n.nodeName === "H1");
      let headingTwo = slots[3]
        .assignedNodes()
        .filter((n) => n.nodeName === "H2");
      if (headingOne.length > 0) {
        this.slidePanel.setAttribute(
          "aria-describedby",
          headingOne[0].innerText
        );
      } else if (headingTwo.length > 0) {
        this.slidePanel.setAttribute(
          "aria-describedby",
          headingTwo[0].innerText
        );
      }
    });
    slots[0].addEventListener("slotchange", () => {
      const button = slots[0].assignedNodes();
      button[0].setAttribute("aria-haspopup", true);
      this._checkButtonLink(button[0]);
    });
    this.slidePanel = this.shadowRoot.querySelector(".slidepanel");
    this.slidePanel.addEventListener(
      "animationend",
      this.handleAnimationEnd.bind(this),
      false
    );
    this.slidePanel.style.display = "none";
    this.slidePanel.setAttribute("aria-hidden", true);
    this._watchEscape = this._watchEscape.bind(this);
    document.addEventListener("keydown", this._watchEscape);
  }

  _checkButtonLink(button) {
    if (!button) {
      return;
    }
    const href = button.getAttribute("href");
    if (href && href.indexOf("#") != -1) {
      const hashIndex = href.indexOf("#");
      this.hash = href.substring(hashIndex + 1);
    }

    this._checkLocationUrl();
  }

  _checkLocationUrl() {
    const locationHash = window.location.hash.substr(1);
    if (locationHash && locationHash === this.hash) {
      this.toggleSlidePanel();
    }
  }

  render() {
    return html`
      ${this.hasOwnTrigger
        ? html`
            <slot
              name="trigger"
              class="slideinpanel-trigger"
              @click="${this.toggleSlidePanel}"
            ></slot>
          `
        : ""}
      <div class="slidepanel" role="dialog">
        <div class="slidepanel-header">
          <slot name="header"></slot>
          <button
            aria-label="Close"
            @click="${this._beforeClose}"
            class="slidepanel-close"
          >
            <slot name="close"></slot>
          </button>
        </div>
        <div class="slidepanel-body">
          <slot></slot>
        </div>
        <slot name="footer"></slot>
      </div>
      ${this.closeOnOutsideClick
        ? html`
            <div class="slidepanel-overlay" @click="${this._beforeClose}"></div>
          `
        : html` <div class="slidepanel-overlay"></div> `}
    `;
  }

  toggleSlidePanel() {
    const returnBeforeOpenEvent = this.dispatchEvent(this.beforeOpenEvent);
    if (!returnBeforeOpenEvent) {
      return;
    }
    this.active = !this.active;
    if (this.active) {
      this.slidePanel.setAttribute("aria-hidden", false);
      this.slidePanel.style.display = "flex";
    }
  }

  _watchEscape(event) {
    if (event.key === "Escape") {
      this._beforeClose();
    }
  }

  _beforeClose() {
    const returnBeforeCloseEvent = this.dispatchEvent(this.beforeCloseEvent);
    if (!returnBeforeCloseEvent) {
      return;
    }

    this.closeSlidepanel();
  }

  closeSlidepanel() {
    this.active = false;
    this.slidePanel.setAttribute("aria-hidden", true);
  }

  handleAnimationEnd(ev) {
    if (ev.target != this.slidePanel) return;
    if (
      ev.animationName === "slideOutRight" ||
      ev.animationName === "slideOutLeft"
    ) {
      this.slidePanel.style.display = "none";
      this.dispatchEvent(this.closeEvent);
    } else if (
      ev.animationName === "slideInRight" ||
      ev.animationName === "slideInLeft"
    ) {
      this.slidePanel.focus();
      this.dispatchEvent(this.openEvent);
    }
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          display: var(--slidepanel-display, block);
        }

        .slidepanel {
          position: fixed;
          top: 0;
          bottom: 0;
          width: 100%;
          max-width: var(--slidepanel-max-width, 80%);
          background-color: var(--slidepanel-background-color, white);
          z-index: var(--slidepanel-z-index, 10000);
          flex-direction: column;
          max-height: var(--slidepanel-max-height, 100vh);
          height: var(--slidepanel-height, 100%);
        }

        .slidepanel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: var(
            --slidepanel-header-bordor-bottom,
            1px solid transparent
          );
          padding: var(--slidepanel-header-padding, 1rem);
        }

        .slidepanel-close {
          margin-left: auto;
          background: var(--slidepanel-close-background, transparent);
          border: var(--slidepanel-close-border, none);
          border-radius: var(--slidepanel-close-border-radius, 0);
          padding: var(--slidepanel-close-padding, 0);
          height: var(--slidepanel-close-height, auto);
          width: var(--slidepanel-close-width, auto);
          position: var(--slidepanel-close-position, relative);
          left: var(--slidepanel-close-left, auto);
          right: var(--slidepanel-close-right, auto);
          top: var(--slidepanel-close-top, auto);
          bottom: var(--slidepanel-close-bottom, auto);
          z-index: var(--slidepanel-close-zindex, 1);
          cursor: pointer;
        }

        .slidepanel-body {
          flex: 1;
          overflow-y: auto;
          padding: var(--slidepanel-body-padding, 0);
          position: relative;
          height: var(--slidepanel-body-height, auto);
        }

        :host([styledScrollbar]) .slidepanel-body::-webkit-scrollbar-track {
          box-shadow: var(
            --slidepanel-scrollbar-track-box-shadow,
            inset 0 0 6px rgba(0, 0, 0, 0.3)
          );
          background-color: var(
            --slidepanel-scrollbar-track-background-color,
            #f5f5f5
          );
          border-radius: var(--slidepanel-scrollbar-track-border-radius, 10px);
        }

        :host([styledScrollbar]) .slidepanel-body::-webkit-scrollbar {
          width: var(--slidepanel-scrollbar-width, 12px);
          background-color: var(
            --slidepanel-scrollbar-background-color,
            #f5f5f5
          );
        }

        :host([styledScrollbar]) .slidepanel-body::-webkit-scrollbar-thumb {
          border-radius: var(--slidepanel-scrollbar-thumb-border-radius, 10px);
          box-shadow: var(
            --slidepanel-scrollbar-thumb-box-shadow,
            inset 0 0 6px rgba(0, 0, 0, 0.3)
          );
          background-color: var(
            --slidepanel-scrollbar-thumb-background-color,
            #555
          );
        }

        .slidepanel-overlay {
          opacity: 0;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          background-color: var(
            --slidepanel-overlay-background-color,
            rgba(0, 0, 0, 0.5)
          );
          z-index: calc(var(--slidepanel-z-index, 10000) - 1);
          height: var(--slidepanel-overlay-height, 100%);
          visibility: hidden;
        }

        :host([active]) .slidepanel-overlay {
          opacity: 1;
          visibility: visible;
        }

        :host([position="left"]) .slidepanel-overlay {
          animation: fadeIn 0.3s ease;
        }

        :host([position="left"]) .slidepanel {
          left: 0;
          transform: translate3d(0, -100%, 0);
          border-right: var(--slidepanel-border-right, 1px solid black);
        }

        :host([position="right"]) .slidepanel {
          right: 0;
          transform: translate3d(0, 100%, 0);
          border-left: var(--slidepanel-border-left, 1px solid black);
        }

        :host([position="left"]) .slidepanel {
          animation: slideOutLeft 0.4s ease forwards;
        }

        :host([position="right"]) .slidepanel {
          animation: slideOutRight 0.4s ease forwards;
        }

        :host([position="left"][active]) .slidepanel {
          animation: slideInLeft 0.4s ease forwards;
          left: 0;
        }

        :host([position="right"][active]) .slidepanel {
          animation: slideInRight 0.4s ease forwards;
          right: 0;
        }

        :host([fullWidth]) .slidepanel {
          max-width: 100%;
        }
      `,
    ];
  }
}

window.customElements.get("true-slide-in-panel") ||
  window.customElements.define("true-slide-in-panel", TrueSlideInPanel);
